import { useState } from "react";
import { Globe, ExternalLink } from "lucide-react";

/**
 * Sources - a reusable, trust-leading source block.
 *
 * Leads with an honest "{n} sources" count, then lists each source as a calm,
 * clickable cyan link showing its domain (derived host, sans "www."), prefixed
 * by the domain's favicon. The favicon comes from Google's favicon service and
 * gracefully falls back to a lucide globe glyph if it fails to load.
 *
 * URLs are de-duplicated (so five links to the same page collapse to one row),
 * and every link opens in a new tab with rel="noopener noreferrer".
 *
 * Recency / per-source dates are intentionally NOT shown - the backend does not
 * yet emit per-source dates, so we omit it rather than fabricate it.
 */

interface SourcesProps {
  urls: string[];
  className?: string;
}

/** Derive a clean host (sans "www.") from a URL, or null if unparseable. */
function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** De-duplicate by exact URL, preserving first-seen order. */
function dedupe(urls: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of urls) {
    const url = (u ?? "").trim();
    if (!url || seen.has(url)) continue;
    seen.add(url);
    out.push(url);
  }
  return out;
}

export function Sources({ urls, className = "" }: SourcesProps) {
  const list = dedupe(urls);
  if (list.length === 0) return null;

  return (
    <div className={className}>
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.18em] mb-2.5">
        {list.length} {list.length === 1 ? "source" : "sources"}
      </p>
      <ul className="flex flex-col gap-1.5">
        {list.map((url, i) => (
          <li key={`${url}-${i}`}>
            <SourceLink url={url} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function SourceLink({ url }: { url: string }) {
  const [iconFailed, setIconFailed] = useState(false);
  const host = hostOf(url);
  const label = host ?? url;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={url}
      className="group inline-flex items-center gap-2 min-w-0 max-w-full text-xs text-cyan-400 hover:text-cyan-300 transition-colors cursor-pointer"
    >
      <span className="flex h-4 w-4 items-center justify-center shrink-0">
        {host && !iconFailed ? (
          <img
            src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32`}
            alt=""
            width={16}
            height={16}
            loading="lazy"
            className="h-4 w-4 rounded-sm"
            onError={() => setIconFailed(true)}
          />
        ) : (
          <Globe className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
        )}
      </span>
      <span className="truncate underline decoration-cyan-400/30 underline-offset-2 group-hover:decoration-cyan-300">
        {label}
      </span>
      <ExternalLink className="h-3 w-3 text-slate-600 shrink-0 group-hover:text-cyan-300" aria-hidden="true" />
    </a>
  );
}
